import { Component, inject, signal } from '@angular/core';
import { CommonModule, JsonPipe } from '@angular/common';
import { HttpClient, HttpContext, HttpHeaders, HttpResponse } from '@angular/common/http';
import { FormsModule } from '@angular/forms';
import { 
  ScaModuleTitleComponent,
  ScaCardWorkshopComponent,
  ScaBadgeComponent,
  ScaAlertComponent,
  ScaIconComponent,
  ScaButtonComponent,
  ScaTextFieldModule,
  ScaCheckboxComponent,
  ScaViewMoreComponent
} from '@argo-dev-components-sca-angular/dev-components-sca';
import { 
  SKIP_AUTH, 
  SKIP_MULTILANGUAGE, 
  SKIP_TRACES, 
  SKIP_SECURITY,
  SKIP_CONFIG,
  FORCE_INTERNAL,
  FORCE_EXTERNAL
} from '@argo/common';

interface HttpTestRequest {
  url: string;
  method: 'GET' | 'POST' | 'PUT' | 'DELETE';
  body?: any;
}

interface HttpTestResponse {
  status: number;
  statusText: string;
  headers: { [key: string]: string };
  body: any;
  requestHeaders: { [key: string]: string };
  timestamp: Date;
}

@Component({
  selector: 'app-http-interceptors-test',
  standalone: true,
  imports: [
    CommonModule,
    JsonPipe,
    FormsModule,
    ScaModuleTitleComponent,
    ScaBadgeComponent,
    ScaAlertComponent,
    ScaIconComponent,
    ScaButtonComponent,
    ScaTextFieldModule,
    ScaCheckboxComponent,
    ScaViewMoreComponent
  ],
  templateUrl: './http-interceptors-test.component.html',
  styleUrl: './http-interceptors-test.component.scss'
})
export class HttpInterceptorsTestComponent {
  private http = inject(HttpClient);

  // Request Configuration
  testUrl = signal('https://jsonplaceholder.typicode.com/posts/1');
  testMethod = signal<'GET' | 'POST' | 'PUT' | 'DELETE'>('GET');
  testBody = signal('');

  // HttpContext Tokens (Interceptor Flags)
  skipAuth = signal(false);
  skipMultilanguage = signal(false);
  skipTraces = signal(false);
  skipSecurity = signal(false);
  skipConfig = signal(false);
  forceInternal = signal(false);
  forceExternal = signal(false);

  // Response Data
  loading = signal(false);
  lastResponse = signal<HttpTestResponse | null>(null);
  errorMessage = signal<string | null>(null);

  // Predefined URLs for quick testing
  predefinedUrls = [
    { 
      label: 'JSONPlaceholder API (External)', 
      url: 'https://jsonplaceholder.typicode.com/posts/1',
      description: 'API pública externa sin CORS'
    },
    { 
      label: 'HTTPBin GET (External)', 
      url: 'https://httpbin.org/get',
      description: 'Servicio de prueba que devuelve headers'
    },
    { 
      label: 'HTTPBin Headers (External)', 
      url: 'https://httpbin.org/headers',
      description: 'Muestra todos los headers enviados'
    },
    { 
      label: 'API Simulada Interna', 
      url: 'https://api.segurcaixaadeslas.es/api/test',
      description: 'URL interna simulada (puede fallar)'
    }
  ];

  /**
   * Ejecuta la petición HTTP con las configuraciones seleccionadas
   */
  executeRequest(): void {
    this.loading.set(true);
    this.errorMessage.set(null);
    this.lastResponse.set(null);

    // Construir HttpContext con los flags seleccionados
    const context = new HttpContext();
    if (this.skipAuth()) context.set(SKIP_AUTH, true);
    if (this.skipMultilanguage()) context.set(SKIP_MULTILANGUAGE, true);
    if (this.skipTraces()) context.set(SKIP_TRACES, true);
    if (this.skipSecurity()) context.set(SKIP_SECURITY, true);
    if (this.skipConfig()) context.set(SKIP_CONFIG, true);
    if (this.forceInternal()) context.set(FORCE_INTERNAL, true);
    if (this.forceExternal()) context.set(FORCE_EXTERNAL, true);

    const options = {
      context,
      observe: 'response' as const,
    };

    const url = this.testUrl();
    const method = this.testMethod();

    let request$;
    switch (method) {
      case 'POST':
        const postBody = this.parseBody();
        request$ = this.http.post(url, postBody, options);
        break;
      case 'PUT':
        const putBody = this.parseBody();
        request$ = this.http.put(url, putBody, options);
        break;
      case 'DELETE':
        request$ = this.http.delete(url, options);
        break;
      default: // GET
        request$ = this.http.get(url, options);
    }

    request$.subscribe({
      next: (response: HttpResponse<any>) => {
        this.loading.set(false);
        
        // Extraer headers de la respuesta
        const responseHeaders: { [key: string]: string } = {};
        response.headers.keys().forEach(key => {
          responseHeaders[key] = response.headers.get(key) || '';
        });

        // Intentar obtener los headers enviados (no siempre disponible)
        const requestHeaders: { [key: string]: string } = {};
        // Los headers de request no están disponibles en la respuesta normal
        // Esta es una limitación de HTTP, los mostramos en la consola
        console.log('🔍 Request Headers (check Network tab):', {
          url,
          method,
          context: {
            skipAuth: this.skipAuth(),
            skipMultilanguage: this.skipMultilanguage(),
            skipTraces: this.skipTraces(),
            skipSecurity: this.skipSecurity(),
            skipConfig: this.skipConfig(),
            forceInternal: this.forceInternal(),
            forceExternal: this.forceExternal()
          }
        });

        this.lastResponse.set({
          status: response.status,
          statusText: response.statusText,
          headers: responseHeaders,
          body: response.body,
          requestHeaders,
          timestamp: new Date()
        });
      },
      error: (error) => {
        this.loading.set(false);
        this.errorMessage.set(
          `Error ${error.status || 'Unknown'}: ${error.message || 'Request failed'}`
        );
        console.error('❌ HTTP Request Error:', error);
      }
    });
  }

  /**
   * Parsea el body JSON si es necesario
   */
  private parseBody(): any {
    const bodyText = this.testBody().trim();
    if (!bodyText) return {};
    
    try {
      return JSON.parse(bodyText);
    } catch {
      return { data: bodyText };
    }
  }

  /**
   * Carga una URL predefinida
   */
  loadPredefinedUrl(url: string): void {
    this.testUrl.set(url);
  }

  /**
   * Resetea todos los flags de HttpContext
   */
  resetFlags(): void {
    this.skipAuth.set(false);
    this.skipMultilanguage.set(false);
    this.skipTraces.set(false);
    this.skipSecurity.set(false);
    this.skipConfig.set(false);
    this.forceInternal.set(false);
    this.forceExternal.set(false);
  }

  /**
   * Limpia la respuesta actual
   */
  clearResponse(): void {
    this.lastResponse.set(null);
    this.errorMessage.set(null);
  }

  /**
   * Copia el resultado al portapapeles
   */
  copyToClipboard(text: string): void {
    navigator.clipboard.writeText(text).then(
      () => console.log('✅ Copied to clipboard'),
      (err) => console.error('❌ Failed to copy:', err)
    );
  }

  /**
   * Obtiene el color del badge según el status HTTP
   */
  getStatusBadgeVariant(status: number): 'default' | 'accent' | 'white' | 'error' | 'warning' | 'success' {
    if (status >= 200 && status < 300) return 'success';
    if (status >= 300 && status < 400) return 'accent';
    if (status >= 400 && status < 500) return 'warning';
    if (status >= 500) return 'error';
    return 'default';
  }

  /**
   * Genera el código de ejemplo para la configuración actual
   */
  generateCodeExample(): string {
    const contextLines: string[] = [];
    if (this.skipAuth()) contextLines.push('  .set(SKIP_AUTH, true)');
    if (this.skipMultilanguage()) contextLines.push('  .set(SKIP_MULTILANGUAGE, true)');
    if (this.skipTraces()) contextLines.push('  .set(SKIP_TRACES, true)');
    if (this.skipSecurity()) contextLines.push('  .set(SKIP_SECURITY, true)');
    if (this.skipConfig()) contextLines.push('  .set(SKIP_CONFIG, true)');
    if (this.forceInternal()) contextLines.push('  .set(FORCE_INTERNAL, true)');
    if (this.forceExternal()) contextLines.push('  .set(FORCE_EXTERNAL, true)');

    const contextCode = contextLines.length > 0 
      ? `  context: new HttpContext()
${contextLines.join('\n')},` 
      : '';

    const method = this.testMethod().toLowerCase();
    const body = method === 'post' || method === 'put' ? ', body' : '';

    return `// Llamada HTTP con interceptores configurados
this.http.${method}('${this.testUrl()}'${body}${contextCode ? ', {\n' + contextCode + '\n}' : ''})
  .subscribe({
    next: (response) => console.log('Success:', response),
    error: (error) => console.error('Error:', error)
  });`;
  }
}
